<template>
  <div class="editor-container flex justify-center items-center gap-6 mx-1 md:mx-6 relative" :class="editorWrapperClasses">
    <div v-if="store.isAddBackgroundPanelOpen" class="md:hidden z-20 fixed bottom-0 w-full">
      <DrawerAddBackground @close-panel="toggleAddBackgroundPanel" @reset="restoreOriginalResult" :foreground-type="store.selectedImage.meta.foregroundType" :selected-image-state="props.currentPersistentStore" />
    </div>
    <div v-if="store.isEraseRestorePanelOpen" class="md:hidden z-20 fixed bottom-0 w-full">
      <DrawerEraseRestore :editor="editor" @close-panel="toggleEraseRestorePanel" @reset="restoreOriginalResult" />
    </div>
    <!-- Start Result -->
    <div class="flex flex-col">
      <div class="relative clip">
        <div class="absolute z-10 w-full" style="top: 20px">
          <div class="flex shrink justify-center">
            <Toast v-show="toast">{{ toast }}</Toast>
          </div>
        </div>

        <div ref="cursor" class="cursor pointer-events-none fixed top-0 left-0" :class="cursorClasses" :style="cursorStyles">
          <svg :height="(store.drawSettings.brushSize * props.currentPersistentStore?.zoomLevel) / 100" :width="(store.drawSettings.brushSize * props.currentPersistentStore?.zoomLevel) / 100">
            <circle :cx="(store.drawSettings.brushSize * props.currentPersistentStore?.zoomLevel) / 100 / 2" :cy="(store.drawSettings.brushSize * props.currentPersistentStore?.zoomLevel) / 100 / 2" :r="(store.drawSettings.brushSize * props.currentPersistentStore?.zoomLevel) / 100 / 2" :fill="store.drawSettings.color" stroke-width="0"></circle>
          </svg>
        </div>

        <!-- Overlay in case of error -->
        <div v-if="hasError" class="hidden md:flex absolute top-0 left-0 w-full h-full bg-typo z-20 rounded-lg opacity-80"></div>
        <div v-if="hasError" class="md:hidden"><Error /></div>

        <div ref="editor" v-if="!hasError" :class="draggableCursorClasses" @wheel="handleMouseWheel" @mouseenter="handleEditorMouseEnter" @mouseleave="handleEditorMouseLeave" @mousemove="handleEditorMouseMove">
          <ImageDragger ref="dragger" :value="sliderValue" @update="updateSliderValue" rounded="sm" class="pointer-events-none" :disabled="true" :checker-board-style="{ zIndex: -2 }">
            <template #front>
              <v-stage ref="stage" :config="stageConfig" class="pointer-events-auto flex justify-center">
                <v-layer ref="backgroundLayer" :config="backgroundLayerConfig">
                  <v-image v-if="props.currentPersistentStore?.selectedBackgroundPhotoUrl" ref="backgroundPhoto" :config="backgroundImageConfig" />
                  <v-rect v-else-if="props.currentPersistentStore?.selectedBackgroundColor" :config="backgroundColorRectConfig" />
                </v-layer>
                <v-layer ref="originalBackgroundLayer" :config="originalBackgroundLayerConfig">
                  <v-image :image="currentLayers?.originalBackgroundImage" :config="originalBackgroundImageConfig" />
                </v-layer>
                <v-layer ref="foregroundLayer">
                  <v-image :config="shadowLayerConfig" :image="currentLayers?.shadowImage" />
                  <v-image :config="semiTransparencyLayerConfig" :image="currentLayers?.semiTransparencyImage" />
                  <v-image :image="currentLayers?.foregroundImage" />
                </v-layer>
                <v-layer id="drawingLayer" ref="drawingLayer" :config="drawingLayerConfig">
                  <v-line v-for="line in store.lines" :config="lineConfig(line)"></v-line>
                </v-layer>
              </v-stage>
            </template>
            <template #back>
              <v-stage ref="stageOriginalBg" :config="stageConfig" class="flex justify-center">
                <v-layer>
                  <v-image :image="currentLayers?.originalBackgroundImage" :config="originalBackgroundImageConfig" />
                </v-layer>
              </v-stage>
            </template>
          </ImageDragger>
        </div>

        <div v-if="!hasError" class="hidden">
          <v-stage ref="stageHd" :config="stageConfigHd">
            <v-layer ref="backgroundLayerHd">
              <v-image v-if="getPersistentStore(hdStageImage)?.selectedBackgroundPhotoUrl" ref="backgroundPhotoHD" :config="backgroundImageConfigHD" />
              <v-rect v-else-if="getPersistentStore(hdStageImage)?.selectedBackgroundColor" :config="backgroundColorRectConfigHD" />
            </v-layer>
            <v-layer ref="foregroundLayerHd">
              <v-image :config="shadowLayerConfigHd" :image="layersForImage(hdStageImage)?.shadowImageHd" />
              <v-image v-if="isSemiTransparencyLayerVisible" :image="layersForImage(hdStageImage)?.semiTransparencyImageHd" />
              <v-image :image="layersForImage(hdStageImage)?.foregroundImageHd" />
            </v-layer>
          </v-stage>
        </div>

        <MagicOverlay v-if="isProcessing && !hasError" class="rounded-lg overflow-hidden" />

        <Shine v-if="showShine" class="absolute top-0 left-0 text-brand -translate-x-8 -translate-y-8 hidden lg:block" />
      </div>

      <!-- Start bottom bar Desktop -->
      <div v-if="!hasError" class="hidden md:flex w-100">
        <div class="flex gap-4 items-center py-2 rounded-2xl w-100 justify-center">
          <!-- Zoom Buttons -->
          <div class="flex items-center gap-2 mr-2">
            <span>
              <IconButton variant="secondary" :disabled="!props.currentPersistentStore?.canZoomOut || isProcessing" :title="I18n.t('editor.zoom_out')" @click="zoomOutFromCenter"><MinusIcon /></IconButton>
              <IconButton variant="secondary" :disabled="!props.currentPersistentStore?.canZoomIn || isProcessing" :title="I18n.t('editor.zoom_in')" @click="zoomInToCenter"><PlusIcon /></IconButton>
            </span>
          </div>
          <!-- End Zoom Buttons -->
          <span class="text-secondary text-base h-8 w-[1px] rounded-full bg-secondary"></span>
          <!-- Toggle Compare -->
          <IconButton variant="secondary" :title="I18n.t('editor.hold_to_compare')" :disabled="isProcessing" @mousedown="toggleCompareBefore" @mouseup="toggleCompareAfter" target-size="lg"><CompareIcon :class="{ 'text-brand-typo': store.viewMode == ViewMode.Before && !isProcessing, 'text-typo': store.viewMode == ViewMode.After && !isProcessing }" /></IconButton>
          <!-- End Toggle Compare-->
          <span class="text-secondary text-base h-8 w-[1px] rounded-full bg-secondary"></span>
          <!-- Undo/Redo Buttons -->
          <div class="flex items-center gap-2 mr-2">
            <span>
              <IconButton variant="secondary" :disabled="!props.currentPersistentStore?.canUndo" :title="I18n.t('editor.undo')" @click="props.currentPersistentStore?.undo"><UndoIcon /></IconButton>
              <IconButton variant="secondary" :disabled="!props.currentPersistentStore?.canRedo" :title="I18n.t('editor.redo')" @click="props.currentPersistentStore?.redo"><RedoIcon /></IconButton>
            </span>
          </div>
          <!-- End Undo/Redo Buttons -->
        </div>
      </div>
      <!-- End bottom bar Desktop-->
    </div>
    <!-- End Result -->

    <!-- Start Sidebar Desktop -->
    <div ref="tools" class="hidden md:flex flex-col gap-12" style="min-width: 14rem">
      <!-- Start ToolBox -->
      <div v-if="store.isAddBackgroundPanelOpen">
        <div class="z-20 absolute top-2 w-[328px]">
          <DrawerAddBackground @close-panel="toggleAddBackgroundPanel" @reset="restoreOriginalResult" :foreground-type="store.selectedImage.meta.foregroundType" :selected-image-state="props.currentPersistentStore" />
        </div>
      </div>
      <div v-if="store.isEraseRestorePanelOpen">
        <div class="z-20 absolute top-2 w-[328px]">
          <DrawerEraseRestore :editor="editor" @close-panel="toggleEraseRestorePanel" @reset="restoreOriginalResult" />
        </div>
      </div>
      <!-- End ToolBox -->

      <!-- Start Buttons -->
      <div v-if="enableUI" class="flex flex-col items-start gap-3">
        <ActionButtonPlaceholder v-if="isProcessing"></ActionButtonPlaceholder>
        <ActionButton v-else @click="toggleAddBackgroundPanel()">
          <template #icon>
            <AddIcon v-if="!props.currentPersistentStore?.selectedBackgroundPhotoUrl && !props.currentPersistentStore?.selectedBackgroundColor" />
            <span v-else class="w-full h-full" :style="backgroundActionButtonStyle" />
          </template>
          <template #title>{{ I18n.t(`editor.add_background`) }}</template>
        </ActionButton>
        <ActionButtonPlaceholder v-if="isProcessing"></ActionButtonPlaceholder>
        <span v-if="hasExceededMagicBrushIterations(store.selectedImage, refreshTippy)" class="hover-tooltip" :data-tippy-content="`${I18n.t('ai_brush.error.iterations_exceeded')}`" data-tippy-placement="bottom">
          <ActionButton v-if="!isProcessing" :disabled="hasExceededMagicBrushIterations(store.selectedImage, refreshTippy)">
            <template #icon>
              <BrushIcon />
            </template>
            <template #title>
              {{ I18n.t(`editor.erase_restore`) }}
            </template>
          </ActionButton>
        </span>
        <template v-else>
          <ActionButton v-if="!isProcessing" @click="toggleEraseRestorePanel()" :disabled="hasExceededMagicBrushIterations(store.selectedImage, refreshTippy)">
            <template #icon>
              <BrushIcon />
            </template>
            <template #title>
              {{ I18n.t(`editor.erase_restore`) }}
            </template>
          </ActionButton>
        </template>
        <ActionButton v-if="showShadowToggle" @click="toggleShadow()">
          <template #icon>
            <ShadowIcon :class="props.currentPersistentStore?.isShadowLayerVisible ? 'text-brand-typo' : ''" />
          </template>
          <template #title>{{ I18n.t(`editor.shadow`) }}</template>
        </ActionButton>
        <ActionButtonPlaceholder v-if="isProcessing"></ActionButtonPlaceholder>
        <CanvaCta v-else :getDataURL="getDataURL" :stage="stage" :stageHd="stageHd" :image="store.selectedImage" :reset-zoom="resetZoom" :refresh-all-hd-layers="refreshAllHdLayers" :persistent-store="getPersistentStore(store.selectedImage)" />
      </div>
      <div v-if="enableUI" class="flex flex-col items-center gap-1">
        <ButtonPlaceholder v-if="isProcessing"></ButtonPlaceholder>
        <Button v-else full-width @click="downloadPreview" :loading="isProcessingPreviewDownload">{{ I18n.t("image.download") }}</Button>
        <div class="d-block d-md-inline text-typo-tertiary mb-1" v-if="!isProcessing">
          {{ store.selectedImage.meta.previewWidth }} &times; {{ store.selectedImage.meta.previewHeight }} px
          <span class="hover-tooltip" :data-tippy-content="`<strong>${I18n.t('image.good_quality')}</strong><br/>${I18n.t('image.up_to_025_megapixels')}`" data-tippy-placement="bottom">
            <InfoSmallIcon> </InfoSmallIcon>
          </span>
        </div>
        <div v-else="isProcessing" class="mb-4">
          <div class="flex gap-1 items-center cursor-wait text-typo-tertiary">
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
            &times;
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
          </div>
        </div>
        <template>
          <DownloadHdDialog :editor="editor" :is-processing="isProcessing" :is-processing-hd-download="isProcessingHdDownload" :is-processing-preview-download="isProcessingPreviewDownload" :enable-download-hd-button="enableDownloadHdButton" :credits="credits" :load-credits-and-update-preview="loadCreditsAndUpdatePreview" :download-hd="downloadHd" />
        </template>
        <div v-if="isProcessing">
          <div class="flex gap-1 items-center cursor-wait text-typo-tertiary">
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
            &times;
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
          </div>
        </div>
        <div v-else="enableDownloadHdButton && store.selectedImage.meta.hdWidth && store.selectedImage.meta.hdHeight && !isProcessing" class="d-block d-md-inline center text-typo-tertiary">
          {{ store.selectedImage.meta.hdWidth }} &times; {{ store.selectedImage.meta.hdHeight }} px
          <span class="hover-tooltip" :data-tippy-content="`<strong>${I18n.t('image.best_quality')}</strong><br/>${I18n.t('image.up_to_25_megapixels')}`" data-tippy-placement="bottom">
            <InfoSmallIcon> </InfoSmallIcon>
          </span>
        </div>
        <span v-if="!enableDownloadHdButton && !isProcessing" class="text-typo-tertiary">
          {{ I18n.t("image.not_available") }}
          <span class="hover-tooltip" :data-tippy-content="`${I18n.t('image.please_upload_higher_resolution')}`" data-tippy-placement="bottom">
            <InfoSmallIcon> </InfoSmallIcon>
          </span>
        </span>
      </div>
      <!-- End Buttons -->

      <!-- Start Error -->
      <div v-if="hasError" class="z-20 absolute top-2 w-[328px]">
        <Error />
      </div>
      <!-- End Error -->
    </div>
    <!-- End Sidebar Desktop -->

    <!-- Start bottom bar Mobile -->
    <div class="md:hidden fixed bottom-0 w-full !bg-white !bg-opacity-90 !backdrop-blur-lg">
      <div class="mobile-buttons-wrapper">
        <div id="mobile-buttons" class="flex mx-1 my-2 pr-1.5">
          <template>
            <ActionButton class="sm:!flex-col" :primary="true" v-if="!hasImagePreviewErrored(store) && !isProcessing && store.selectedImage && store.selectedImage.meta.preview" @click="downloadPreview">
              <template #icon>
                <DownloadIcon />
              </template>
              <template #title
                ><span class="text-3xs">{{ I18n.t("editor.download") }}</span></template
              >
            </ActionButton>
            <div class="flex gap-1 items-start justify-center cursor-wait" v-else>
              <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse"></div>
            </div>
          </template>

          <template>
            <MobileHdDownload v-if="!isProcessing && showMobileDownloadHdIcon()" :name="imagePreviewName" :preview="store.selectedImage.meta.preview" :editor="editor" :is-processing-hd-download="isProcessingHdDownload" :credits="credits" :download-hd="downloadHd" :isProcessingHdDownload="isProcessingHdDownload" :load-credits-and-update-preview="loadCreditsAndUpdatePreview" :show-button="!isProcessing && showMobileDownloadHdIcon()" />
            <div class="flex gap-1 items-start justify-center cursor-wait" v-else>
              <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse test"></div>
            </div>
          </template>

          <ActionButton class="sm:!flex-col" @click="toggleAddBackgroundPanel()">
            <template #icon>
              <IconButton v-if="!props.currentPersistentStore?.selectedBackgroundPhotoUrl && !props.currentPersistentStore?.selectedBackgroundColor" class="!bg-white">
                <img :src="addBackgroundImageIcon" />
              </IconButton>

              <span v-else class="w-full h-full" :style="backgroundActionButtonStyle" />
            </template>
            <template #title
              ><span class="text-3xs">{{ I18n.t(`editor.add_background`) }}</span></template
            >
          </ActionButton>
          <span v-if="hasExceededMagicBrushIterations(store.selectedImage, refreshTippy)" class="hover-tooltip flex justify-center" :data-tippy-content="`${I18n.t('ai_brush.error.iterations_exceeded')}`" data-tippy-placement="bottom">
            <ActionButton class="sm:!flex-col" :disabled="hasExceededMagicBrushIterations(store.selectedImage, refreshTippy)">
              <template #icon>
                <BrushIcon />
              </template>
              <template #title
                ><span class="text-3xs">{{ I18n.t(`editor.erase_restore`) }}</span></template
              >
            </ActionButton>
          </span>
          <template v-else>
            <ActionButton class="sm:!flex-col" @click="toggleEraseRestorePanel()">
              <template #icon>
                <BrushIcon />
              </template>
              <template #title
                ><span class="text-3xs">{{ I18n.t(`editor.erase_restore`) }}</span></template
              >
            </ActionButton>
          </template>
          <ActionButton class="sm:!flex-col" v-if="showShadowToggle" @click="toggleShadow()">
            <template #icon>
              <ShadowIcon :class="props.currentPersistentStore?.isShadowLayerVisible ? 'text-brand-typo' : ''" />
            </template>
            <template #title
              ><span class="text-3xs">{{ I18n.t(`editor.shadow`) }}</span></template
            >
          </ActionButton>
          <span>
            <CanvaCta :getDataURL="getDataURL" :stage="stage" :stageHd="stageHd" :image="store.selectedImage" :reset-zoom="resetZoom" :refresh-all-hd-layers="refreshAllHdLayers" :persistent-store="getPersistentStore(store.selectedImage)" />
          </span>
        </div>
      </div>
    </div>
    <!-- End bottom bar Mobile -->
  </div>
  <TrustComponent ref="trustComponent" @enqueueWithToken="enqueueWithToken" style="position: absolute"></TrustComponent>
</template>

<script setup lang="ts">
import "@/src/i18n";
import { ref, computed, onMounted, onBeforeUnmount, inject, watch, nextTick, reactive, set } from "vue";
import emitter from "@/modules/event_bus";

import { Image as HTMLImage, Shine, MagicOverlay, ActionButton, ActionButtonPlaceholder, Button, ButtonPlaceholder, ImageDragger, BrushIcon, DownloadIcon, AddIcon, ShadowIcon, Toast, InfoSmallIcon, IconButton, CompareIcon, RedoIcon, UndoIcon, PlusIcon, MinusIcon } from "prism";
import TrustComponent from "@/components/upload/trustcomponent.vue";
import CanvaCta from "@/components/canva_cta.vue";
import DrawerAddBackground from "@/components/upload/drawer_add_background.vue";
import DrawerEraseRestore from "@/components/upload/drawer_erase_restore.vue";
import MobileDownload from "@/components/upload/mobile_download.vue";
import MobileHdDownload from "@/components/upload/mobile_hd_download.vue";
import Error from "@/components/upload/error.vue";
import DownloadHdDialog from "../prism/download_hd_dialog.vue";

import { extractLayers, clearLayers, hasMagicBrushError, getMagicBrushError, ResultVariant, ProcessingError } from "@/modules/internal_api/image";
import type { Image } from "@/modules/internal_api/image";
import { ProcessingState, UploadState, originalUrl, hasExceededMagicBrushIterations } from "@/modules/internal_api/image";
import { downloadBlob, downloadURI, getNestedKey, releaseCanvas, shareBlob } from "@/modules/utils";

import { ViewMode, useEditorStore } from "@/stores/editor_store";
import addBackgroundImageIcon from "@assets/images/icons/icon-add-image.svg";

import { DrawAction } from "@/stores/line";
import type { Line, Point } from "@/stores/line";
import Client from "@/modules/internal_api/client";
import Routes from "@/modules/routes";
import Flipper from "@/modules/flipper";
import tippy from "tippy.js";

import * as StackBlur from "stackblur-canvas";
import { fetchImage } from "@/modules/utils";

import { rbgEditorAppliedBrushV100, rbgEditorFailedMagicBrushV100, rbgImageDownloadV100 } from "kaleido-event-tracker";

import Konva from "konva";
Konva.hitOnDragEnabled = true;

interface EditorProps {
  persistentStores: any; // TODO: assign proper type here.
  currentPersistentStore: any;
}

const props = defineProps<EditorProps>();
const store = useEditorStore();
const I18n = inject("I18n");

const workers = ref([]);
const toast = ref<String>(null);

// UI state
const isShadowLayerVisible = ref<Boolean>(true);
const isSemiTransparencyLayerVisible = ref<Boolean>(true);
const backgroundPhoto = ref<any>(undefined);
const backgroundPhotoHD = ref<any>(undefined);

const enableDownloadHdButton = computed(() => {
  return store.selectedImage.meta.fullAvailable;
});

const editor = ref<HTMLElement>();
const cursor = ref<HTMLElement>();
let minScale;
let maxScale;

onMounted(async () => {
  store.drawSettings.isEnabled = false;
  store.isAddBackgroundPanelOpen = false;
  store.isEraseRestorePanelOpen = false;

  props.currentPersistentStore?.setZoom(100);
  minScale = stage.value.getStage().scaleX();
  maxScale = minScale * 4;

  if (store.selectedImage.previewResult.state === ProcessingState.Finished) {
    await refreshAllLayers();
    // fitStageIntoParentContainer();
    // revealResult(); // temporarily disabling revealResult on mounted to avoid a flash and reveal of the old image when uploading a new one. ideally there is a way to distinguish the case where an image is being uploaded and the case where we just visit the page.
  }

  refreshTippy();
  emitter.emit("editorMounted");

  window.addEventListener("pagehide", function (_event) {
    cleanup();
  });

  window.addEventListener("pageshow", function (_event) {
    window.location.reload();
  });

  hideNavBar();
});

const cleanup = () => {
  // This is mostly for iOS because it doesn't seem to clean up the canvases properly
  // It;s not a full fix but reduces the retained size from 1MB to around 400KB
  [foregroundLayer, backgroundLayer, originalBackgroundLayer, drawingLayer, foregroundLayerHd, backgroundLayerHd].forEach((layer) => {
    const stage = layer.value?.getStage();
    const canvas = stage?.getCanvas()?._canvas;
    releaseCanvas(canvas);
    stage?.destroyChildren();
  });

  const internalCanvases = [];

  const shapes = Konva.shapes;
  for (var key in shapes) {
    const shape = shapes[key];
    internalCanvases.push(shape?.parent?.canvas?._canvas);
    internalCanvases.push(shape?.parent?.hitCanvas?._canvas);
    internalCanvases.push(shape?.parent?.bufferCanvas?._canvas);
    internalCanvases.push(shape?.parent?.bufferHitCanvas?._canvas);
  }

  const internalStages = Konva.stages;
  internalStages.forEach((stage) => {
    internalCanvases.push(stage?.canvas?._canvas);
    internalCanvases.push(stage?.hitCanvas?._canvas);
    internalCanvases.push(stage?.bufferCanvas?._canvas);
    internalCanvases.push(stage?.bufferHitCanvas?._canvas);
  });

  internalCanvases.forEach((canvas) => {
    releaseCanvas(canvas);
  });

  stage.value?.getStage()?.destroyChildren();
  stageHd.value?.getStage()?.destroyChildren();

  stage.value?.getStage()?.destroy();
  stageHd.value?.getStage()?.destroy();
};

const hideNavBar = () => {
  // There is a navbar from desktop that has larger height which shows up underneath the current header.
  // So we need to make sure for inline editor this header is not displayed in mobile screens.
  const navEl = document.getElementById("navbar");
  navEl.classList.add("hidden");
  navEl.classList.add("md:block");
};

onBeforeUnmount(() => {
  cleanup();
});

const isSpacing = ref<Boolean>(false);

const refreshTippy = () => {
  tippy("[data-tippy-content]", { allowHTML: true, interactive: true });
};

const showShadowToggle = computed(() => {
  const shadowImage = currentLayers.value?.shadowImage;
  return shadowImage && Flipper.isEnabled("shadow_toggle");
});

document.onkeydown = function (e) {
  if ((e.key === "z" || e.key == "Z") && e.shiftKey && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    props.currentPersistentStore?.redo();
  } else if ((e.key === "z" || e.key == "Z") && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    props.currentPersistentStore?.undo();
  }
  if (e.key == "+" && !(e.ctrlKey || e.metaKey)) {
    if (props.currentPersistentStore?.canZoomIn) {
      props.currentPersistentStore?.zoomIn();
      applyZoom();
      2;
    }
    if (lastMouseEvent) updateCursorPosition(lastMouseEvent);
  }
  if (e.key == "-" && !(e.ctrlKey || e.metaKey)) {
    if (props.currentPersistentStore?.canZoomOut) {
      props.currentPersistentStore?.zoomOut();
      applyZoom();
    }
    if (lastMouseEvent) updateCursorPosition(lastMouseEvent);
  }
  if (e.key == " " || e.key == "Meta" || e.key == "Ctrl") {
    isSpacing.value = true;
  }
};

document.onkeyup = function (e) {
  if (e.key == " " || e.key == "Meta" || e.key == "Ctrl") {
    isSpacing.value = false;
  }
};

const handleEditorMouseMove = (e: MouseEvent) => {
  // const theStage = stage.value.getStage();
  // theStage.setPointersPositions(e);
  if (store.drawSettings.isEnabled) {
    updateCursorPosition(e);
  }
};

let lastMouseEvent;
const updateCursorPosition = (e: MouseEvent) => {
  const x = e.pageX - (store.drawSettings.brushSize * props.currentPersistentStore?.zoomLevel) / 100 / 2;
  const y = e.pageY - (store.drawSettings.brushSize * props.currentPersistentStore?.zoomLevel) / 100 / 2;
  store.cursorPosition = { x, y };
  lastMouseEvent = e;
};

const handleEditorMouseEnter = (e: MouseEvent) => {
  store.isHoveringEditor = true;
  // const theStage = stage.value.getStage();
  // theStage.setPointersPositions(e);
};

const handleEditorMouseLeave = (e: MouseEvent) => {
  store.isHoveringEditor = false;
  // const theStage = stage.value.getStage();
  // theStage.setPointersPositions(e);
};

const editorWrapperClasses = computed(() => {
  return {
    "overflow-y-hidden": !hasError.value && store.isSwitchingImages,
  };
});

const cursorStyles = computed(() => {
  const isHoveringRelevantElement = store.isHoveringEditor || store.isHoveringBrushSizeSlider;
  const isVisible = isHoveringRelevantElement && store.drawSettings.isEnabled && !isSpacing.value;
  const opacity = isVisible ? 0.9 : 0;

  const styles = {
    opacity: opacity,
    transform: `translate(${store.cursorPosition.x}px, ${store.cursorPosition.y}px)`,
  };
  return {
    opacity: opacity,
    transform: `translate(${store.cursorPosition.x}px, ${store.cursorPosition.y}px)`,
  };
});

const cursorClasses = computed(() => {
  return {
    block: store.isHoveringBrushSizeSlider,
    "hidden md:block": !store.isHoveringBrushSizeSlider,
  };
});

const draggableCursorClasses = computed(() => {
  let zoomLevel = props.currentPersistentStore?.zoomLevel;

  return {
    "cursor-draggable": zoomLevel > 100 && (!store.isEraseRestorePanelOpen || (store.isEraseRestorePanelOpen && isSpacing.value)),
  };
});

const dragger = ref<InstanceType<typeof ImageDragger>>();
const sliderValue = ref<number>(0);

const revealResult = () => {
  dragger?.value?.transition(100, 0, 2000);
};

const hideResult = (image: Image) => {
  updateLayerRef(image, { foregroundImage: null });
  dragger?.value?.transition(0, 100, 1000);
};

const updateSliderValue = (value: number) => {
  sliderValue.value = value;
};

const revealResultFromCurrentPosition = () => {
  dragger?.value?.transition(sliderValue.value, 0, 300);
};

const hideResultFromCurrentPosition = () => {
  dragger?.value?.transition(sliderValue?.value, 100, 300);
};

const trustComponent = ref<InstanceType<typeof TrustComponent>>();

const credits = ref<Number>(0);
const loadCreditsAndUpdatePreview = async () => {
  resetZoom();
  refreshPreviewFromStage();
  const url = Routes.get("credits_info_url");
  fetch(url)
    .then((res) => res.json())
    .then((data) => {
      credits.value = data.credits;
    });
};

// Konva
// There are currently no exported types for these layers :(
// https://github.com/konvajs/vue-konva/issues/135
const stage = ref();
const stageOriginalBg = ref();

const foregroundLayer = ref();
const backgroundLayer = ref();
const originalBackgroundLayer = ref();
const drawingLayer = ref();

const stageHd = ref();
const foregroundLayerHd = ref();
const backgroundLayerHd = ref();

type SzeneSize = { width: number; height: number };
const szeneSize = computed((): SzeneSize => {
  const image: Image | null = store.selectedImage;
  // Fall back to 16:9ish for placeholder image
  let size = {
    width: 614,
    height: 406,
  };

  if (image && image.meta.previewWidth && image.meta.previewHeight) {
    size = {
      width: image.meta.previewWidth,
      height: image.meta.previewHeight,
    };
  }

  return size;
});

const szeneSizeHD = computed((): SzeneSize => {
  return szeneSize.value;
});

const stageConfigHd = computed(() => {
  const size: SzeneSize = szeneSizeHD.value;

  return {
    width: size.width,
    height: size.height,
  };
});

const stageConfig = computed(() => {
  const size: SzeneSize = szeneSize.value;
  return {
    width: size.width,
    height: size.height,
    onMouseDown: handleMouseDown,
    onTouchStart: handleMouseDown,
    onMouseMove: handleMouseMove,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };
});

const originalBackgroundLayerConfig = computed(() => {
  const config = {
    visible: store.drawSettings.isEnabled && store.drawSettings.action === DrawAction.Restore,
    opacity: 0.5,
  };

  return config;
});

const backgroundLayerConfig = computed(() => {
  return {
    visible: !store.drawSettings.isEnabled || (store.drawSettings.isEnabled && store.drawSettings.action === DrawAction.Erase),
    opacity: store.isSwitchingImages ? 0 : 1,
  };
});

const shadowLayerConfig = computed(() => {
  if (isComparing.value && !store.isEraseRestorePanelOpen) {
    return {
      visible: true,
      opacity: 1 - sliderValue.value / 100,
    };
  }

  return {
    visible: props.currentPersistentStore?.isShadowLayerVisible && !store.drawSettings.isEnabled,
  };
});

const shadowLayerConfigHd = computed(() => {
  if (!hdStageImage.value) return null;
  const image = hdStageImage.value;

  return {
    visible: getPersistentStore(image)?.isShadowLayerVisible,
  };
});

const semiTransparencyLayerConfig = computed(() => {
  return {
    visible: isSemiTransparencyLayerVisible.value,
  };
});

const backgroundColorRectConfig = computed(() => {
  const size = szeneSize.value;

  return {
    fill: props.currentPersistentStore?.selectedBackgroundColor,
    width: size.width,
    height: size.height,
  };
});

const backgroundColorRectConfigHD = computed(() => {
  if (!hdStageImage.value) return null;
  const image = hdStageImage.value;

  const size = {
    width: image.meta.hdWidth,
    height: image.meta.hdHeight,
  };

  return {
    fill: getPersistentStore(image)?.selectedBackgroundColor,
    width: size.width,
    height: size.height,
  };
});

const backgroundActionButtonStyle = computed(() => {
  if (props.currentPersistentStore?.selectedBackgroundPhotoUrl) {
    return {
      "background-image": `url('${props.currentPersistentStore?.selectedBackgroundPhotoUrl}')`,
      "background-size": "cover",
    };
  } else {
    return {
      "background-color": props.currentPersistentStore?.selectedBackgroundColor,
    };
  }
});

const originalBackgroundImageConfig = computed(() => {
  const originalBackgroundImage = currentLayers.value?.originalBackgroundImage;
  if (!originalBackgroundImage) return null;

  const size: SzeneSize = szeneSize.value;
  const config = {
    width: size.width,
    height: size.height,
    image: originalBackgroundImage,
  };

  return config;
});

const backgroundImageConfig = computed(() => {
  const backgroundImage = currentLayers.value?.backgroundImage;
  if (!backgroundImage) return null;

  const config = {
    width: calculateCoverSize.value.width,
    height: calculateCoverSize.value.height,
    x: calculateCoverSize.value.x,
    y: calculateCoverSize.value.y,
    image: backgroundImage,
  };

  return config;
});

const backgroundImageConfigHD = computed(() => {
  if (!hdStageImage.value) return null;

  const layers = layersForImage(hdStageImage.value);
  const backgroundImage = layers.backgroundImage;

  if (!backgroundImage) return null;

  const config = {
    width: calculateCoverSizeHD.value.width,
    height: calculateCoverSizeHD.value.height,
    x: calculateCoverSizeHD.value.x,
    y: calculateCoverSizeHD.value.y,
    image: backgroundImage,
  };

  return config;
});

const handleMouseWheel = async (event: WheelEvent) => {
  event.preventDefault();
  event.stopPropagation();
  const theStage = stage.value.getStage();
  const pointerPosition = theStage.getPointerPosition();
  const oldScale = theStage.scaleX();

  const focusPoint = {
    x: pointerPosition.x / oldScale - theStage.x() / oldScale,
    y: pointerPosition.y / oldScale - theStage.y() / oldScale,
  };

  if (event.deltaY < 0 && props.currentPersistentStore?.canZoomIn) {
    props.currentPersistentStore?.zoomIn();
    applyZoom();
  }
  if (event.deltaY > 0 && props.currentPersistentStore?.canZoomOut) {
    props.currentPersistentStore?.zoomOut();
    applyZoom();
  }

  repositionAfterFocusPointZoom(focusPoint, pointerPosition);
};

const repositionAfterFocusPointZoom = (focusPoint: Point, pointerPosition: Point) => {
  const theStage = stage.value.getStage();
  const stages = [theStage, stageOriginalBg.value.getStage()];
  const scale = theStage.scaleX();

  let x = -(focusPoint.x - pointerPosition.x / scale) * scale;
  let y = -(focusPoint.y - pointerPosition.y / scale) * scale;

  x = Math.floor(x);
  y = Math.floor(y);

  if (props.currentPersistentStore?.zoomLevel === 100) {
    x = 0;
    y = 0;
  }

  let pos = { x, y };
  pos = clampPos(pos);

  stages.forEach((stage) => {
    stage.position(pos);
  });
};

const stageFitRequested = ref<Boolean>(false);
const fitStageIntoParentContainer = () => {
  if (!stage.value) return;
  if (stageFitRequested.value) return;
  stageFitRequested.value = true;

  const theStage = stage.value.getStage();
  const stages = [theStage, stageOriginalBg.value.getStage()];

  let { width, height, scale } = editingAreaSize();
  stages.forEach((stage) => {
    stage.width(width);
    stage.height(height);
    stage.scale({ x: scale, y: scale });
  });

  applyZoom();
  stageFitRequested.value = false;
};

const applyZoom = () => {
  const theStage = stage.value.getStage();
  const stages = [theStage, stageOriginalBg.value.getStage()];

  const baseScale = initalScale.value;
  const zoomLevel = props.currentPersistentStore?.zoomLevel;
  let newScale = (baseScale * zoomLevel) / 100;
  newScale = Math.round((newScale + Number.EPSILON) * 1000) / 1000;

  stages.forEach((stage) => {
    stage.scale({ x: newScale, y: newScale });
  });

  if (lastMouseEvent) updateCursorPosition(lastMouseEvent);
};

const bottomBar = ref<HTMLElement>();
const tools = ref<HTMLElement>();
const initalScale = ref<number | null>(null);
const editingAreaSize = () => {
  const size: SzeneSize = szeneSize.value;

  const navbarHeight = document.getElementById("navbar")?.clientHeight || 0;
  const mobileNavbarHeight = document.getElementById("new-header")?.clientHeight || 0;
  const footerHeight = document.getElementById("footer")?.clientHeight || 0;

  const windowSize = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  const bottomBarSize = {
    width: bottomBar.value?.clientWidth || 0,
    height: bottomBar.value?.clientHeight || 0,
  };

  const toolsSize = {
    width: tools.value?.clientWidth || 0,
    height: tools.value?.clientHeight || 0,
  };

  // Average size of the gap between the elements
  const gap = 20;

  const maxEditorHeight = Math.min(windowSize.height - Math.max(navbarHeight, mobileNavbarHeight) - footerHeight - bottomBarSize.height - 9 * gap, size.height);
  const maxEditorWidth = Math.min(windowSize.width - toolsSize.width - 28, size.width);

  const heightScale = maxEditorHeight / size.height;
  const widthScale = maxEditorWidth / size.width;
  let scale = Math.min(heightScale, widthScale);

  let newWidth = size.width * scale;
  let newHeight = size.height * scale;

  // console.log("navbarHeight", navbarHeight, "footerHeight", footerHeight, "windowSize", windowSize, "bottomBarSize", bottomBarSize, "toolsSize", toolsSize, "gap", gap, "maxEditorHeight", maxEditorHeight, "maxEditorWidth", maxEditorWidth, "heightScale", heightScale, "widthScale", widthScale, "scale", scale, "newWidth", newWidth, "newHeight", newHeight);
  initalScale.value = scale;

  const newSize = {
    width: newWidth,
    height: newHeight,
    scale: scale,
  };

  return newSize;
};

window.addEventListener("resize", fitStageIntoParentContainer);

// Drawing mode
const isDrawing = ref<Boolean>(false);
const magicMaskTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

// Panning mode
const isPanning = ref<Boolean>(false);

const pointerPosition = () => {
  const theStage = stage.value.getStage();
  const pointerPosition = theStage.getPointerPosition();
  const stageTransform = theStage.getAbsoluteTransform().copy();
  const stagePosition = stageTransform.invert().point(pointerPosition);
  return { x: stagePosition.x, y: stagePosition.y };
};

const handleMouseDown = (event) => {
  if (magicMaskTimeout.value) {
    clearTimeout(magicMaskTimeout.value);
  }

  if (store.drawSettings.isEnabled && !isSpacing.value) {
    isDrawing.value = true;
    const position: Point = pointerPosition();
    if (!position) return;
    store.startLine(position);
  } else {
    isPanning.value = true;
  }
};

const handleTouchMove = (event) => {
  if (isDrawing.value) {
    handleBrush(event);
    return;
  }
  const realEvent = event.evt || event;

  realEvent.preventDefault();
  realEvent.stopPropagation();

  if (realEvent.touches.length === 1) {
    handleOneTouchMove(realEvent);
  } else if (realEvent.touches.length === 2) {
    handleTwoTouchMove(realEvent);
  }
};

const prevTouches = ref<Point[] | null>(null);
const handleTwoTouchMove = async (event) => {
  const theStage = stage.value.getStage();
  const stages = [theStage, stageOriginalBg.value.getStage()];

  const stagePos = theStage.position();
  const scale = theStage.scaleX();

  const touches = theStage.getPointersPositions();
  const currentTouch1 = touches[0];
  const currentTouch2 = touches[1];

  if (!prevTouches.value) {
    prevTouches.value = [currentTouch1, currentTouch2];
    return;
  }

  // Panning
  const prevCenter = getCenter(prevTouches.value[0], prevTouches.value[1]);
  const currentCenter = getCenter(currentTouch1, currentTouch2);
  const localCurrentCenter = {
    x: (currentCenter.x - stagePos.x) / scale,
    y: (currentCenter.y - stagePos.y) / scale,
  };

  touchStartPos.value = currentCenter;

  // Zooming
  const prevDistance = getDistance(prevTouches.value[0], prevTouches.value[1]);
  const currentDistance = getDistance(currentTouch1, currentTouch2);

  const distance = Math.abs(currentDistance - prevDistance);

  const isZoomingIn = currentDistance > prevDistance;
  const zoomFactor = distance;

  if (isZoomingIn) {
    if (props.currentPersistentStore?.canZoomIn) {
      props.currentPersistentStore?.zoomIn(zoomFactor);
      applyZoom();
    }
  } else {
    if (props.currentPersistentStore?.canZoomOut) {
      props.currentPersistentStore?.zoomOut(zoomFactor);
      applyZoom();
    }
  }

  const newScale = theStage.scaleX();

  const delta = {
    x: currentCenter.x - prevCenter.x,
    y: currentCenter.y - prevCenter.y,
  };

  let newPos = {
    x: currentCenter.x - localCurrentCenter.x * newScale + delta.x,
    y: currentCenter.y - localCurrentCenter.y * newScale + delta.y,
  };

  newPos = clampPos(newPos);
  stages.forEach((stage) => {
    stage.position(newPos);
  });

  prevTouches.value = [currentTouch1, currentTouch2];
};

const handleTouchEnd = (event) => {
  prevTouches.value = null;
};

const getDistance = (p1: Point, p2: Point) => {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

const getCenter = (p1: Point, p2: Point): Point => {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
};

const zoomOutFromCenter = async () => {
  if (props.currentPersistentStore?.canZoomOut) {
    props.currentPersistentStore?.zoomOut();
    applyZoom();
    repositionAfterCenterZoom();
  }
};

const zoomInToCenter = async () => {
  if (props.currentPersistentStore?.canZoomIn) {
    props.currentPersistentStore?.zoomIn();
    applyZoom();
    repositionAfterCenterZoom();
  }
};

const repositionAfterCenterZoom = () => {
  const theStage = stage.value.getStage();
  const stages = [theStage, stageOriginalBg.value.getStage()];
  const scale = theStage.scaleX();
  const stageWidth = theStage.width();
  const stageHeight = theStage.height();

  const imageWidth = store.selectedImage.meta.previewWidth;
  const imageHeight = store.selectedImage.meta.previewHeight;

  let x = (-imageWidth * scale) / 2 + stageWidth / 2;
  let y = (-imageHeight * scale) / 2 + stageHeight / 2;

  x = Math.floor(x);
  y = Math.floor(y);

  if (props.currentPersistentStore?.zoomLevel === 100) {
    x = 0;
    y = 0;
  }

  let pos = { x, y };
  pos = clampPos(pos);

  stages.forEach((stage) => {
    stage.position(pos);
  });
};

const resetZoom = () => {
  props.currentPersistentStore?.setZoom(100);
  applyZoom();
  repositionAfterCenterZoom();
};

const touchStartPos = ref<Point>({ x: 0, y: 0 });
const handleOneTouchMove = (event) => {
  if (props.currentPersistentStore?.zoomLevel === 100) return;

  const theStage = stage.value.getStage();
  const stages = [theStage, stageOriginalBg.value.getStage()];
  const stageTransform = theStage.getAbsoluteTransform().copy();
  const stagePosition = stageTransform.invert().point(theStage.getPointerPosition());

  const oldCenter = {
    x: stagePosition.x - theStage.x(),
    y: stagePosition.y - theStage.y(),
  };

  let movementX = 0;
  let movementY = 0;

  if (event.touches) {
    const touches = theStage.getPointersPositions();
    const touch = touches[0];

    movementX = touch.x - touchStartPos.value.x || 0;
    movementY = touch.y - touchStartPos.value.y || 0;
    touchStartPos.value = touch;

    // TODO: fix one touch move
    return;
  } else {
    movementX = event.movementX || 0;
    movementY = event.movementY || 0;
  }

  const newCenter = {
    x: oldCenter.x - movementX,
    y: oldCenter.y - movementY,
  };

  let pos = {
    x: stagePosition.x - newCenter.x,
    y: stagePosition.y - newCenter.y,
  };

  pos = clampPos(pos);
  stages.forEach((stage) => {
    stage.position(pos);
  });
};

const clampPos = (pos: Point): Point => {
  const theStage = stage.value.getStage();
  const canvasWidth = theStage.width();
  const canvasHeight = theStage.height();
  const scale = theStage.scaleX();
  const contentWidth = store.selectedImage.meta.previewWidth;
  const contentHeight = store.selectedImage.meta.previewHeight;

  const scaledWidth = contentWidth * scale;
  const scaledHeight = contentHeight * scale;

  const minX = Math.min(0, canvasWidth - scaledWidth);
  const maxX = scaledWidth > canvasWidth ? 0 : canvasWidth - scaledWidth;
  const minY = Math.min(0, canvasHeight - scaledHeight);
  const maxY = scaledHeight > canvasHeight ? 0 : canvasHeight - scaledHeight;

  pos.x = Math.max(Math.min(pos.x, maxX), minX);
  pos.y = Math.max(Math.min(pos.y, maxY), minY);

  return pos;
};

const handleBrush = (event) => {
  const position: Point = pointerPosition();
  if (!position) return;
  store.addLinePoint(position);
};

const handleMouseMove = (event: MouseEvent) => {
  if (isDrawing.value) {
    handleBrush(event);
    return;
  }

  if (isPanning.value) {
    var theEvent = event.evt || event; // konva event or javascript event
    if (theEvent) {
      handleOneTouchMove(theEvent);
    }
  }
};

const handleMouseUp = (event) => {
  if (isDrawing.value) {
    isDrawing.value = false;

    const eventMode = store.isMagicBrushEnabled ? "magic" : "ordinary";
    const eventType = store.drawSettings.action === DrawAction.Erase ? "erase" : "restore";
    if (store.drawSettings.action === DrawAction.Erase) {
      window.track("Editor", "pixels_erased", "Pixels erased");
    } else {
      window.track("Editor", "pixels_restored", "Pixels restored");
    }
    rbgEditorAppliedBrushV100({ image_id: store.selectedImage.meta.id, mode: eventMode, action_type: eventType, brush_size: store.drawSettings.brushSize });

    if (store.lines.length === 0) return;

    magicMaskTimeout.value = setTimeout(() => {
      enqueueMagicBrush();
    }, 500);
  } else {
    // lastCenter = null;
    isPanning.value = false;
  }
};

let drawingLayerOpacity = ref<any>(0.7);

const enqueueMagicBrush = async () => {
  // Generate a random id for this iteration
  const iterationId = Math.random().toString(36).substring(5);

  const image = store.selectedImage;
  drawingLayerOpacity.value = 1.0;
  await nextTick();
  const mask = drawingLayer.value.getNode().toDataURL();
  drawingLayerOpacity.value = 0.7;
  const maskBlob: Blob = await fetch(mask).then((r) => r.blob());

  let alphaBytes: Uint8Array;
  let alphaUrl: string;
  const currentPersistentStore = getPersistentStore(image);
  const variant = currentPersistentStore?.previewVariant;
  if (variant === ResultVariant.MagicBrushPreview) {
    // The last iteration might have had an error
    if (image.magicBrushPreviewResult.state == ProcessingState.Error) {
      alphaUrl = image.meta.ai_brush_iterations[image.meta.ai_brush_last_iteration].alpha;
    } else {
      if (!image.magicBrushPreviewResult?.layers?.foreground?.alphaBlob) {
        // refresh the foreground image if layers are not there anymore because the error on the previous iteration removed the property
        await refreshForegroundImage(variant, image);
      }
      alphaBytes = image.magicBrushPreviewResult?.layers?.foreground?.alphaBytes;
    }
  } else {
    if (!image.previewResult?.layers?.foreground?.alphaBlob) {
      // refresh the foreground image if layers are not there anymore because the error on the previous iteration removed the property
      await refreshForegroundImage(variant, image);
    }
    alphaBytes = image.previewResult?.layers?.foreground?.alphaBytes;
  }

  const cachedState = JSON.parse(JSON.stringify(getPersistentStore(image)?.current));
  let updatedImagePromise: Promise<Image> = Client.enqueueMagicBrush(image, maskBlob, alphaBytes, alphaUrl, store.drawSettings.action, iterationId, store.isMagicBrushEnabled);
  image["magicBrushPreviewResult"].state = ProcessingState.Processing;
  store.updateImage(image);
  let updatedImage = await updatedImagePromise;

  // we deliberately don't check for errors here because this just initiates the AiBrushWorker and only sets the ai_brush_proccessing_started_at.
  // Actual data from the worker will be fetched and checked inside the poll
  updatedImage = await poll(updatedImage, ResultVariant.MagicBrushPreview);
  store.clearLines();

  if (updatedImage.magicBrushPreviewResult.state === ProcessingState.Finished) {
    // TODO: Now that we are updating changes to local currentPersistentStore,
    //   I'm not sure it will actually reflect the changes to the actual persistentStores

    currentPersistentStore?.selectMagicBrushVariant();
    currentPersistentStore?.cacheMagicBrush({ iterationId: iterationId });
    currentPersistentStore?.snapshotWithState(cachedState);
    currentPersistentStore?.persist();

    if (store.selectedImage.meta.id !== updatedImage.meta.id) {
      return;
      // Don't wanna process no more.
    }

    refreshPreviewFromStage();
  }
};

const handleProcessingError = (processingError: ProcessingError) => {
  store.drawSettings.isEnabled = true;
  let message = "";

  if (processingError.code == "other" && (processingError.message == "no_foreground_detected" || processingError.message == "no_foreground_updates")) {
    let translationId = "ai_brush.error." + processingError.message;
    translationId += store.drawSettings.action === DrawAction.Erase ? "_erase" : "_restore";
    message = I18n.t(translationId);
    rbgEditorFailedMagicBrushV100({ image_id: store.selectedImage.meta.id, error_message: message });
  } else if (processingError.code == "other" && processingError.message == "sorry_something_went_wrong_please_try_again_later") {
    const translationId = "ai_brush.error." + processingError.message;
    message = I18n.t(translationId);
  } else {
    message = processingError.message;
  }

  showToast(message);
};

const showToast = (message) => {
  toast.value = message;
  setTimeout(() => {
    toast.value = null;
  }, 3000);
};

const drawingLayerConfig = computed(() => {
  return {
    visible: store.drawSettings.isEnabled,
    opacity: drawingLayerOpacity.value || 0.7,
  };
});

const defaultLineConfig = computed(() => {
  return {
    stroke: store.drawSettings.color,
    strokeWidth: store.drawSettings.brushSize,
    lineCap: "round",
    lineJoin: "round",
  };
});

const lineConfig = (line: Line) => {
  const points = line.points
    .map((point: Point) => {
      return [point.x, point.y];
    })
    .flat();

  return {
    points,
    ...defaultLineConfig.value,
  };
};

const refreshPreviewFromStage = () => {
  if (previewNeedsRefresh.value === false) {
    // console.log("Preview does not need refresh");
    return;
  }
  // console.log("Refreshing preview from stage");
  try {
    const image: Image = store.selectedImage; // no zoom for previews
    image.meta.livePreview = getDataURL(image, stage.value.getStage());
    store.updateImage(image);
  } catch (e) {
    return;
  }
};

const refreshLayer = async (variant: ResultVariant, layerName: string, resultRef: string, image: Image): Promise<void> => {
  // console.log("Refreshing layer", layerName, "for variant", variant);
  return new Promise(async (resolve, reject) => {
    const relevantState = getNestedKey(image, `${variant}.state`);
    const isMagicBrushResult = variant === ResultVariant.MagicBrushPreview || variant === ResultVariant.MagicBrushHd;

    if (relevantState !== ProcessingState.Finished && !isMagicBrushResult) {
      return resolve();
    }

    const layers = getNestedKey(image, `${variant}.layers`);
    let updatedImage = image;
    if (!layers) {
      // console.log("No layers found, extracting them for image", image.meta.id, "and variant", variant);
      updatedImage = await extractLayers(image, variant, getPersistentStore(image));
      store.updateImage(updatedImage);
    }

    const img: HTMLImageElement = new Image();
    const layer = getNestedKey(updatedImage, `${variant}.layers.${layerName}`);
    if (!layer.blob) {
      // This image does not have a layer with this name (e.g. shadow or semiTransparency)
      // Consider this a success as there is nothing to update
      return resolve();
    }

    const blob = layer.blob;
    img.src = URL.createObjectURL(blob);
    img.onload = () => {
      updateLayerRef(updatedImage, { [resultRef]: img });

      return resolve();
    };
  });
};

const refreshForegroundImage = async (variant: ResultVariant, image: Image): Promise<void> => {
  await nextTick();

  if (hasError.value) {
    const img: HTMLImageElement = new Image();

    img.src = store.selectedImage.meta.preview;
    img.onload = () => {
      updateLayerRef(image, { foregroundImage: img });
      fitStageIntoParentContainer();
      return Promise.resolve();
    };
  }

  return refreshLayer(variant, "foreground", "foregroundImage", image);
};

const refreshShadowImage = async (variant: ResultVariant, image: Image): Promise<void> => {
  return refreshLayer(variant, "shadow", "shadowImage", image);
};

const refreshSemitransparencyImage = async (variant: ResultVariant, image: Image): Promise<void> => {
  return refreshLayer(variant, "semiTransparency", "semiTransparencyImage", image);
};

const refreshOriginalBackgroundImage = async (): Promise<void> => {
  return new Promise((resolve) => {
    const image = store.selectedImage;
    if (image.original.state !== UploadState.Finished) {
      resolve();
      return;
    }

    const size: SzeneSize = szeneSize.value;
    const source = image.original.url;
    const img = new Image();
    img.src = source;
    img.crossOrigin = "anonymous";
    img.width = size.width;
    img.height = size.height;

    img.onload = () => {
      updateLayerRef(image, { originalBackgroundImage: img });
      resolve();
    };
  });
};

const refreshBackgroundImage = async (image: Image): Promise<void> => {
  // console.log("Refreshing layer backgroundImage");
  const source = getPersistentStore(image)?.selectedBackgroundPhotoUrl;
  const isBackgroundBlurEnabled = getPersistentStore(image)?.isBackgroundBlurEnabled;
  const blurRadius = parseInt(getPersistentStore(image)?.blurRadius);

  if (source === null) return;

  const backgroundImage = await fetchImage(source);

  if (isBackgroundBlurEnabled) {
    return new Promise(async (resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = backgroundImage.width;
      canvas.height = backgroundImage.height;
      ctx.drawImage(backgroundImage, 0, 0);

      StackBlur.canvasRGB(canvas, 0, 0, canvas.width, canvas.height, blurRadius);
      const resultImage = new Image();
      resultImage.crossOrigin = "anonymous";
      resultImage.src = canvas.toDataURL("image/png");

      resultImage.onload = () => {
        updateLayerRef(image, { backgroundImage: resultImage });
        releaseCanvas(canvas);
        resolve();
      };
    });
  }

  return new Promise((resolve) => {
    updateLayerRef(image, { backgroundImage: backgroundImage });
    resolve();
  });
};

const layerRefreshRequested = ref<Boolean>(false);
const refreshAllLayers = async (force: boolean = false): Promise<void> => {
  if (layerRefreshRequested.value && !force) {
    return;
  }

  layerRefreshRequested.value = true;
  await refreshRemoteLayers(store.selectedImage);
  await refreshLocalLayers(store.selectedImage);

  await refreshPreviewFromStage();
  layerRefreshRequested.value = false;
};

const refreshRemoteLayers = async (image: Image): Promise<void> => {
  // console.log("Refreshing remote layers");
  updateLayerRef(image, {
    foregroundImage: null,
    shadowImage: null,
    semiTransparencyImage: null,
    originalBackgroundImage: null,
  });

  // make sure changes are drawn
  await nextTick();
  foregroundLayer.value?.getNode()?.batchDraw();

  const variant: ResultVariant = getPersistentStore(image)?.previewVariant;

  // Refresh foreground image first so that the layers are extracted only once
  // Otherwise the shadow and semi transparency layers would extract the layers from the zip file again
  await refreshForegroundImage(variant, image);

  await nextTick();
  fitStageIntoParentContainer();

  const promises = [refreshShadowImage(variant, image), refreshSemitransparencyImage(variant, image)];
  await Promise.allSettled(promises);
};

const refreshLocalLayers = async (image: Image): Promise<void> => {
  const promises = [];
  if (getPersistentStore(image)?.selectedBackgroundPhotoUrl) {
    promises.push(refreshBackgroundImage(image));
  }

  promises.push(refreshOriginalBackgroundImage());
  await Promise.allSettled(promises);
};

type ImageRefs = {
  foregroundImage?: HTMLImageElement | null;
  shadowImage?: HTMLImageElement | null;
  semiTransparencyImage?: HTMLImageElement | null;
  backgroundImage?: HTMLImageElement | null;
  originalBackgroundImage?: HTMLImageElement | null;
  foregroundImageHd?: HTMLImageElement | null;
  shadowImageHd?: HTMLImageElement | null;
  semiTransparencyImageHd?: HTMLImageElement | null;
};

let layerRef: { [key: string]: ImageRefs } = reactive({});

const updateLayerRef = (image: Image, value: ImageRefs) => {
  const existingRefs = layerRef[image.meta.id] || reactive({});
  for (const key in value) {
    Reflect.set(existingRefs, key, value[key]);
  }
  Reflect.set(layerRef, image.meta.id, existingRefs);
};

const currentLayers = computed(() => {
  return layerRef?.[store.selectedImage?.meta.id];
});

const layersForImage = (image: Image) => {
  if (!image) return null;

  return layerRef?.[image?.meta.id];
};

watch(
  () => currentLayers.value?.backgroundImage,
  async (value) => {
    if (!value) return;
    await nextTick();
    await refreshPreviewFromStage();
  },
  { deep: true }
);

watch(
  () => store.viewMode,
  () => {
    if (store.viewMode == ViewMode.After) {
      revealResultFromCurrentPosition();
    }
    if (store.viewMode == ViewMode.Before) {
      hideResultFromCurrentPosition();
    }
  }
);

watch(
  () => store.images,
  () => {
    emitter.emit("imagesChanged", store.images.length);
  }
);
const previewNeedsRefresh = ref(true);
store.$onAction(async ({ name, after, args }) => {
  if (name === "selectImage") {
    const image = args[0] as Image;
    if (image?.meta?.id === store.selectedImage?.meta?.id) {
      return;
    }
    store.isSwitchingImages = true;
    previewNeedsRefresh.value = false;
    store.isAddBackgroundPanelOpen = false;
    store.isEraseRestorePanelOpen = false;
    store.drawSettings.isEnabled = false;

    updateLayerRef(image, { backgroundImage: null });

    after(async () => {
      try {
        await refreshAllLayers();
        repositionAfterCenterZoom();
        previewNeedsRefresh.value = true;
        await refreshPreviewFromStage();
        fitStageIntoParentContainer();
      } catch (e) {
      } finally {
        localStorage.setItem("lastSelectedImageId", image.meta.id);
        previewNeedsRefresh.value = true;
        layerRefreshRequested.value = false;
        store.isSwitchingImages = false;
      }
    });
  }
});

const hdStageImage = ref<Image | null>(null);
const refreshAllHdLayers = async (image: Image): Promise<void> => {
  updateLayerRef(image, {
    foregroundImageHd: null,
    shadowImageHd: null,
    semiTransparencyImageHd: null,
  });

  const variant: ResultVariant = props.currentPersistentStore?.hdVariant;

  // Refresh foreground image first so that the layers are extracted only once
  // Otherwise the shadow and semi transparency layers would extract the layers from the zip file again
  await refreshForegroundImageHd(variant, image);
  await Promise.allSettled([refreshShadowImageHd(variant, image), refreshSemitransparencyImageHd(variant, image)]);

  hdStageImage.value = image;
};

const refreshForegroundImageHd = async (variant: ResultVariant, image: Image): Promise<void> => {
  return refreshLayer(variant, "foreground", "foregroundImageHd", image);
};

const refreshShadowImageHd = async (variant: ResultVariant, image: Image): Promise<void> => {
  return refreshLayer(variant, "shadow", "shadowImageHd", image);
};

const refreshSemitransparencyImageHd = async (variant: ResultVariant, image: Image): Promise<void> => {
  return refreshLayer(variant, "semiTransparency", "semiTransparencyImageHd", image);
};

const toggleAddBackgroundPanel = async (): Promise<void> => {
  if (isProcessingHdDownload.value && downloadHDQueue[0]?.meta.id === store.selectedImage.meta.id) return;
  props.currentPersistentStore?.setZoom(100);
  applyZoom();
  repositionAfterCenterZoom();

  store.isAddBackgroundPanelOpen = !store.isAddBackgroundPanelOpen;

  if (store.isAddBackgroundPanelOpen === true) {
    revealResultFromCurrentPosition();
  } else {
    await nextTick();
    refreshTippy();
  }
};

const isComparing = ref(false);
const toggleCompareBefore = (): void => {
  store.viewMode = ViewMode.Before;
  isComparing.value = true;
};

const toggleCompareAfter = (): void => {
  store.viewMode = ViewMode.After;
  isComparing.value = false;
};

const toggleEraseRestorePanel = async (): Promise<void> => {
  if (isProcessingHdDownload.value && downloadHDQueue[0]?.meta.id === store.selectedImage.meta.id) return;

  props.currentPersistentStore?.setZoom(100);
  applyZoom();
  repositionAfterCenterZoom();

  store.isEraseRestorePanelOpen = !store.isEraseRestorePanelOpen;
  store.drawSettings.isEnabled = !store.drawSettings.isEnabled;

  if (store.isEraseRestorePanelOpen === true) {
    revealResultFromCurrentPosition();
  } else {
    await nextTick();
    refreshPreviewFromStage();
    refreshTippy();
  }
};

const restoreOriginalResult = (): void => {
  props.currentPersistentStore?.reset();
};

const toggleShadow = (): void => {
  const isVisible = props.currentPersistentStore?.isShadowLayerVisible;

  props.currentPersistentStore?.withSnapshot(() => {
    props.currentPersistentStore?.setShadowLayerVisible(!isVisible);
  });
};

const toggleSemitransparency = (): void => {
  isSemiTransparencyLayerVisible.value = !isSemiTransparencyLayerVisible.value;
};

const imagePreviewName = computed(() => {
  const image: Image = store.selectedImage;
  if (image.previewResult.state !== ProcessingState.Finished) {
    return;
  }

  return image.previewResult.name;
});

// Downloads
const isProcessingPreviewDownload = ref(false);
const downloadPreview = async (_, share: Boolean = false) => {
  const image: Image = store.selectedImage;
  if (image.previewResult.state !== ProcessingState.Finished) {
    return;
  }

  isProcessingPreviewDownload.value = true;

  if (!share) {
    window.track("Images", "download_from_editor_sd", "Download from Editor SD");
    rbgImageDownloadV100({ image_id: image.meta.id, resolution: "standard", download_location: "inline_editor" });

    getPersistentStore(image)?.setZoom(100);
    applyZoom();
    repositionAfterCenterZoom();

    const dataURL = getDataURL(image, stage.value.getStage());

    await downloadURI(dataURL, image.previewResult.name + ".png");
    window.track("Images", "download_from_editor", "Download from Editor");
  } else {
    const blob = await stage.value.getStage().toBlob();
    shareBlob(blob, image.previewResult.name + ".png");
  }

  isProcessingPreviewDownload.value = false;
};

const sharePreview = () => {
  downloadPreview(null, true);
};

const timeouts: Map<string, any> = new Map();

const getDataURL = (image: Image, stage: any): string => {
  let oldScale = stage.scaleX();
  stage.scale({ x: 1, y: 1 });
  let dataURL = stage.toDataURL({ x: 0, y: 0, width: image.meta.previewWidth, height: image.meta.previewHeight });
  stage.scale({ x: oldScale, y: oldScale });
  return dataURL;
};

const poll = async (image: Image, variant: ResultVariant): Promise<Image> => {
  const stateKey: string = `${variant}.state`;
  const nextPollKey: string = `${variant}.nextFetchIn`;
  const timeoutKey: string = `${variant}.${image.meta.id}.timeout`;

  // console.log("Start polling for image", image.meta.id, "and variant", variant);
  const executePoll = async (image, resolve, reject): Promise<void> => {
    const updatedImage: Image = await Client.getImage(image.meta.id);

    const isMagicBrushVariant = [ResultVariant.MagicBrushPreview].includes(variant);

    // console.log("Received updated image", updatedImage.meta.id, "with state", getNestedKey(updatedImage, stateKey), "and next poll in", getNestedKey(updatedImage, nextPollKey), "ms");
    if (isMagicBrushVariant && hasMagicBrushError(updatedImage)) {
      const processingError: ProcessingError = getMagicBrushError(updatedImage);
      store.clearLines();
      handleProcessingError(processingError);
      store.selectedImage.magicBrushPreviewResult.state = ProcessingState.Error;
      return;
    }

    store.updateImage(updatedImage);
    const state = getNestedKey(updatedImage, stateKey) as ProcessingState;
    const isFinished = [ProcessingState.Finished, ProcessingState.Error].includes(state);

    if (isFinished === true) {
      clearTimeout(timeouts.get(timeoutKey));
      timeouts.delete(timeoutKey);
      return resolve(updatedImage);
    } else {
      const timeout = setTimeout(async () => {
        executePoll(updatedImage, resolve, reject);
      }, getNestedKey(updatedImage, nextPollKey));

      timeouts.set(timeoutKey, timeout);
    }
  };

  return new Promise((resolve, reject) => {
    executePoll(image, resolve, reject);
  });
};

const isProcessingHdDownload = ref(false);
const downloadHDQueue = ref<Image[]>([]);

const downloadHd = async (closeDialog?: () => void) => {
  if (closeDialog) closeDialog();
  let image: Image = store.selectedImage;
  downloadHDQueue.value.push(image);
};

watch(
  downloadHDQueue,
  async (queue) => {
    if (queue.length === 0) return;
    let image = queue[0];

    // TODO: keep individual processing state for each image to allow multiple downloads at the same time
    isProcessingHdDownload.value = true;
    const variant: ResultVariant = getPersistentStore(image)?.hdVariant;

    let downloadFilename = "";
    if (variant === ResultVariant.Hd) {
      if (image.hdResult.state !== ProcessingState.Finished) {
        image = await Client.processHd(image);

        if (image.hdResult.state === ProcessingState.Error) {
          isProcessingHdDownload.value = false;
          console.error("TODO: there was an error requesting the hd version", image.hdResult);
          return;
        }
        store.updateImage(image);
        image = await poll(image, ResultVariant.Hd);
        emitter.emit("purchase");
      }

      if (image.hdResult.state !== ProcessingState.Finished) {
        isProcessingHdDownload.value = false;
        console.log("TODO: there was an error requesting the hd version", image.hdResult);
        return;
      }

      downloadFilename = image.hdResult.name;
    } else {
      image = await Client.enqueueMagicBrushHd(image, getPersistentStore(image));
      store.updateImage(image);
      image = await poll(image, ResultVariant.MagicBrushHd);

      downloadFilename = image.magicBrushHdResult.name;
    }

    await refreshAllHdLayers(image);
    const stage = stageHd.value.getStage();
    const dataURL = stage.toDataURL({ x: 0, y: 0, width: image.meta.hdWidth, height: image.meta.hdHeight });

    window.track("Images", "download_from_editor_hd", "Download from Editor HD");
    rbgImageDownloadV100({ image_id: image.meta.id, resolution: "high-definition", download_location: "inline_editor" });

    await downloadURI(dataURL, downloadFilename + ".png");
    window.track("Images", "download_from_editor", "Download from Editor");

    downloadHDQueue.value.shift();
    isProcessingHdDownload.value = false;
  },
  { deep: true }
);

//Display checks
const showShine = computed(() => {
  return !store.isAddBackgroundPanelOpen && !store.isEraseRestorePanelOpen && !hasError.value && !isProcessing.value;
});

const isProcessing = computed(() => {
  return isPreviewProcessing.value || isMagicBrushPreviewProcessing.value;
});

const isPreviewProcessing = computed(() => {
  return store.selectedImage?.previewResult?.state === ProcessingState.Processing;
});

watch(
  () => isPreviewProcessing.value,
  async () => {
    await nextTick();
    refreshTippy();
  }
);

const isMagicBrushPreviewProcessing = computed(() => store.selectedImage?.magicBrushPreviewResult?.state === ProcessingState.Processing);

const hasError = computed(() => {
  const image = store.selectedImage;
  if (!image) return false;

  const originalCheck = image.original.state === UploadState.Error;
  const previewCheck = image.previewResult.state === ProcessingState.Error;

  return originalCheck || previewCheck;
});

const enableUI = computed(() => {
  return !hasError.value && !store.isAddBackgroundPanelOpen && !store.isEraseRestorePanelOpen;
});

const imageWidth = computed(() => {
  switch (store.selectedImage?.meta?.orientation) {
    case "landscape":
      return 614;
    case "portrait":
      return 408;
    case "square":
      return 500;
    default:
      return 614;
  }
});

watch(
  () => props.currentPersistentStore?.selectedBackgroundColor,
  async (selectedBackgroundColor) => {
    if (selectedBackgroundColor && props.currentPersistentStore?.selectedBackgroundPhotoUrl) props.currentPersistentStore?.setSelectedBackgroundPhotoUrl(undefined);
    if (selectedBackgroundColor !== undefined) {
      await nextTick();
      refreshPreviewFromStage();
    }
  },
  { deep: true }
);

watch(
  () => props.currentPersistentStore?.selectedBackgroundPhotoUrl,
  async (selectedBackgroundPhotoUrl) => {
    if (selectedBackgroundPhotoUrl && props.currentPersistentStore?.selectedBackgroundColor) props.currentPersistentStore?.setSelectedBackgroundColor(undefined);
    if (selectedBackgroundPhotoUrl !== undefined) {
      await refreshBackgroundImage(store.selectedImage);
    }
  },
  { deep: true }
);

watch(
  () => props.currentPersistentStore?.blurRadius,
  async () => {
    if (props.currentPersistentStore?.selectedBackgroundPhotoUrl) {
      await refreshBackgroundImage(store.selectedImage);
    }
  },
  { deep: true }
);

watch(
  () => props.currentPersistentStore?.isBackgroundBlurEnabled,
  async () => {
    if (props.currentPersistentStore?.selectedBackgroundPhotoUrl) {
      await refreshBackgroundImage(store.selectedImage);
    } else if (props.currentPersistentStore?.isBackgroundBlurEnabled) {
      props.currentPersistentStore?.setSelectedBackgroundColor(undefined);
      props.currentPersistentStore?.setSelectedBackgroundPhotoUrl(originalUrl(store.selectedImage));
    }
  },
  { deep: true }
);

watch(
  () => props.currentPersistentStore?.previewVariant,
  async () => {
    if (layerRefreshRequested.value) return;

    const image = store.selectedImage;
    await refreshAllLayers();
    // if (image.hdResult?.state === ProcessingState.Finished) {
    //   await refreshAllHdLayers();
    // }
  },
  { deep: true }
);

watch(
  () => props.currentPersistentStore?.magicBrushCache?.iterationId,
  async () => {
    if (!props.currentPersistentStore?.magicBrushCache?.iterationId) return;

    const iterationId = store.selectedImage.meta?.ai_brush_last_iteration;
    const iterationHistory = store.selectedImage?.meta.ai_brush_iterations;
    const iteration = iterationHistory?.[iterationId];

    if (!iteration) return;

    const image = clearLayers(store.selectedImage, ResultVariant.MagicBrushPreview);
    store.updateImage(image);
    await refreshAllLayers();
    // if (iteration.hd) {
    //   await refreshAllHdLayers();
    // }
  },
  { deep: true }
);

const hasImagePreviewErrored = (store) => {
  if (store.selectedImage.previewResult.state === "error") return true;

  return false;
};

const showMobileDownloadHdIcon = () => !!store.selectedImage && !hasImagePreviewErrored(store) && !!store.selectedImage.meta.preview;

const calculateCoverSize = computed(() => {
  const backgroundImage = currentLayers.value?.backgroundImage;
  const szene = szeneSize.value;
  if (!backgroundImage) return { width: szene.width, height: szene.height };

  const scale = Math.max(szene.width / backgroundImage.width, szene.height / backgroundImage.height);
  const x = szene.width / 2 - (backgroundImage.width / 2) * scale;
  const y = szene.height / 2 - (backgroundImage.height / 2) * scale;

  return { width: backgroundImage.width * scale, height: backgroundImage.height * scale, x, y };
});

const calculateCoverSizeHD = computed(() => {
  const image = store.selectedImage;
  const backgroundImage = currentLayers.value?.backgroundImage;

  const szene = {
    width: image.meta.hdWidth,
    height: image.meta.hdHeight,
  };

  if (!backgroundImage) return { width: szene.width, height: szene.height };

  const scale = Math.max(szene.width / backgroundImage.width, szene.height / backgroundImage.height);
  const x = szene.width / 2 - (backgroundImage.width / 2) * scale;
  const y = szene.height / 2 - (backgroundImage.height / 2) * scale;

  return { width: backgroundImage.width * scale, height: backgroundImage.height * scale, x, y };
});

const getPersistentStore = (image: Image) => {
  if (!image) return null;
  return props.persistentStores.get(image.meta.id);
};

const enqueueWithToken = async (image) => {
  emit("enqueueWithToken", image);
};

const emit = defineEmits<{
  (e: "enqueueWithToken", image: Image): void;
}>();

// Expose to parent
defineExpose({
  revealResult,
  hideResult,
  refreshForegroundImage,
  refreshAllLayers,
  refreshRemoteLayers,
  trustComponent,
  handleMouseDown,
  handleMouseUp,
  handleMouseMove,
  toggleCompareBefore,
  toggleCompareAfter,
});
</script>

<style>
.konvajs-content > canvas:first-child {
  z-index: -1;
}

.checkerboard {
  z-index: -2;
}

.checkerboard + div {
  z-index: 1;
}

.prism-imagedragger input[type="range"]::-webkit-slider-thumb {
  position: relative;
  z-index: 1;
}

.cursor {
  z-index: 30;
  -webkit-transition: opacity 0.2s ease-in-out;
  -moz-transition: opacity 0.2s ease-in-out;
  transition: opacity 0.2s ease-in-out;
}

*[style*="display: none"] {
  display: none !important;
}

.overflow-y-hidden {
  overflow-y: hidden;
}

.hover-tooltip > svg {
  display: inline;
}

.cursor-draggable {
  cursor: move; /* fallback: no `url()` support or images disabled */
  cursor: url(images/grab.cur); /* fallback: Chrome 1-21, Firefox 1.5-26, Safari 4+, IE, Edge 12-14, Android 2.1-4.4.4 */
  cursor: grab; /* W3C standards syntax, all modern browser */
}

.cursor-draggable:active {
  cursor: url(images/grabbing.cur);
  cursor: grabbing;
}

.clip {
  clip-path: inset(0 0 0 0);

  @media (min-width: 460px) {
    & {
      clip-path: none;
    }
  }
}

/* TODO: implement proper fix */
/* #mobile\=buttons > button:nth-child(1) > span.font-bold.text-2xs.sm\:text-sm.w-full.text-center.sm\:text-left.font-body.\!no-underline.\!text-typo.transition.ease-in-out.group-hover\:\!text-typo-secondary.sm\:no-touch-hover\:group-hover\:translate-x-0\.5 */
#mobile-buttons button span.text-2xs {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
